// The hud lab (`?scene=hud`, phase D2, gates D2.1 and D2.2): the streamed valley (the default scene) with the
// discovery module in its kernel game, and the HUD, the world-space UI, the minimap and the map screen over it (mounted
// by main.ts through src/hud/index.ts). The integrator makes this the default scene's own: the discovery module joins
// the world's modules and the HUD mounts on the default scene.
import type { SpikeContent } from "../content";
import { teleportHero } from "../world/teleport";
import type { WorldExtra, WorldMount } from "../world/scene";
import { readMapContent, type MapContent } from "./content";
import { createDiscoveryModule, type DiscoverySim } from "./discovery";

/** The hud lab's share of the world scene: the discovery module, and getters valid once the scene is built. */
export interface HudWorld {
  extra: WorldExtra;
  discovery(): DiscoverySim;
  map(): MapContent;
  mount(): WorldMount;
}

/** Build the hud lab's extra for the world scene. */
export function hudWorld(content: SpikeContent): HudWorld {
  let sim: DiscoverySim | null = null;
  let map: MapContent | null = null;
  let mount: (() => WorldMount) | null = null;
  const need = <T>(v: T | null, what: string): T => {
    if (!v) throw new Error(`hud: ${what} not set up yet`);
    return v;
  };
  return {
    extra: ({ rig, mount: m }) => {
      mount = m;
      const wm = (): WorldMount => m();
      map = readMapContent(content, wm().world);
      const d = createDiscoveryModule({
        rig: () => rig,
        map,
        heightAt: (x, z) => wm().world.heightAt(x, z),
        engaged: () => wm().fight().engaged(),
        indoors: () => wm().places().state().interiors.inside !== null,
        teleport: (x, z, yaw) => teleportHero(wm(), x, z, yaw),
      });
      sim = d.sim;
      return { modules: [d.module], build: d.build, hashed: d.hashed };
    },
    discovery: () => need(sim, "the discovery"),
    map: () => need(map, "the map"),
    mount: () => need(mount, "the world")(),
  };
}
